import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Container, Row, Col } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import FormDropdown from '../../../../../shared/components/form/FormDropdown';
import FormCheckboxGroup from '../../../../../shared/components/form/FormCheckboxGroup';
import TextArea from '../../../../../shared/components/form/TextArea';
import { Topic } from '../../../../../services/types/Toolkit';
import { safeFind } from '../../../../../shared/misc/utils';
import { FlowCommentForm } from '../../../../../services/types/Flow';
import { Button } from '../../../../../shared/components';

interface Props {
  operationId: number;
  topics: Topic[];
  handleSubmit: (values: FlowCommentForm) => void;
  isSubmitting: boolean;
  topicTitle: string;
}

const CommentForm = (props: Props) => {
  const { operationId, topics, handleSubmit, isSubmitting, topicTitle } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const formStateInitial = {
    operationId,
    topic: 'Gender',
    type: ['none'],
    description: '',
    keywords: ['none'],
  };

  const commentTypes = [
    { id: 1, text: 'Compliance Issue', value: 'Compliance Issue' },
    {
      id: 2,
      text: 'Quality Improvement',
      value: 'Quality Improvement',
    },
  ];

  const getKeywordsDiv = (topicValue: string) => {
    if (!topics) {
      return null;
    }

    const { keywords } = safeFind(topics, (x) => x.description === topicValue);
    if (!keywords || keywords.length === 0) {
      return null;
    }

    const list = keywords.map((x, i) => ({ id: i + 1, text: x.name, value: x.id.toString() }));

    return (
      <Row>
        <Col md={12}>
          <p>{t('keywords')}:</p>
          <div className="d-flex align-items-start keywords">
            <FormCheckboxGroup name="keywords" elements={list} />
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <Formik
      initialValues={formStateInitial}
      validationSchema={Yup.object().shape({
        description: Yup.string()
          .trim()
          .max(10000, t('validateMaxLength', { len: 10000 }))
          .required(t('required')),
        type: Yup.array().min(2, t('validateCommentType')),
      })}
      onSubmit={(values) => {
        const valuesForSubmit = {
          ...values,
          type: [...values.type],
          keywords: [...values.keywords],
        };
        valuesForSubmit.type.splice(0, 1); // delete 'none' value from array
        valuesForSubmit.keywords.splice(0, 1); // delete 'none' value from array
        handleSubmit(valuesForSubmit);
      }}
    >
      {(formik) => (
        <form className="form" onSubmit={formik.handleSubmit}>
          <Container>
            <Row>
              <Col md={3}>
                <FormDropdown
                  label={<p>{topicTitle}:</p>}
                  name="topic"
                  elements={topics.map((x) => ({
                    id: x.id,
                    text: x.description,
                    value: x.description,
                  }))}
                  value={formik.values.topic}
                  className="no-modal"
                  onChangeAction={() => formik.setFieldValue('keywords', ['none'])}
                />
              </Col>
              <Col md={9}>
                <p>{t('commentType')}:</p>
                <div className="d-flex">
                  <FormCheckboxGroup name="type" elements={commentTypes} />
                </div>
                {formik.touched.type && formik.errors.type && (
                  <span className="form__form-group-error">{formik.errors.type}</span>
                )}
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <TextArea name="description" placeholder={t('commentsPlaceholder')} resize />
              </Col>
            </Row>
            {getKeywordsDiv(formik.values.topic)}
            <hr />
            <Button
              key="btnSubmit"
              className="btn-primary"
              text={t('create')}
              type="submit"
              disabled={isSubmitting || !formik.dirty}
            />
            <Button
              key="btnCancel"
              className="btn-tertiary"
              onClick={() => {
                navigate(-1);
              }}
              text={t('cancel')}
              type="button"
              disabled={formik.isSubmitting}
            />
          </Container>
        </form>
      )}
    </Formik>
  );
};

export default CommentForm;
